import clsx from "clsx";
import React, { useRef } from "react";
import { Card, CardProps, Typography, Box, Button } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { ClientFormCardItem } from "./ClientFormCardItem";
import { useQuery } from "@tanstack/react-query";
import ClientFormsClientService from "../../services/ClientFormsClientService";
import { ClientFormCardItemSkeleton } from "./ClientFormCardItemSkeleton";
import { CardPagination } from "../pagination/CardPagination";
import { ReactComponent as FormsIcon } from "../../icons/FormsProfile.svg";
import { JourneyAnchors, UserRole } from "../../constants";
import ClientDetailsCardHeader from "../coach-clients/ClientDetailsCardHeader";
import { ProfileIconBlock } from "../coach-clients/ProfileIconBlock";
import { SendHorizontal } from "lucide-react";
import { useCurrentUserRole } from "../../hooks/useCurrentUser";
import { useNavigate } from "react-router-dom";
import { COACH_CLIENTS_FORMS_ROUTE } from "../../routes/routes";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    containerType: "inline-size",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    boxSizing: "border-box",
    [theme.containerQueries.down("md")]: {
      gap: theme.spacing(2),
    },
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    marginTop: theme.spacing(1),
  },
  paginationWrapper: {
    marginTop: "auto",
  },

  noText: {
    fontSize: 16,
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
}));

export interface ClientFormsCardProps extends CardProps {
  clientId?: number;
  isPreview?: boolean;
  isFirst?: boolean;
  collapsable?: boolean;
  defaultOpen?: boolean;
}

export const CLIENT_FORMS_CARD_QUERY_KEY = "client-forms-profile-card-query";
const PROFILE_FORMS_CARD_PAGE_SIZE = 4;

export function ClientFormsCard(props: ClientFormsCardProps) {
  const {
    className,
    clientId,
    isPreview,
    isFirst,
    collapsable,
    defaultOpen,
    ...other
  } = props;
  const s = useStyles();
  const role = useCurrentUserRole();
  const [currentPage, setCurrentPage] = React.useState(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpenContent, setIsOpenContent] = React.useState(!!defaultOpen);
  const { data: clientFormsData, isLoading: clientFormsQueryInFlight } =
    useQuery({
      queryKey: [CLIENT_FORMS_CARD_QUERY_KEY, { clientId, currentPage }],
      queryFn: () =>
        ClientFormsClientService.getFormsList(
          currentPage,
          PROFILE_FORMS_CARD_PAGE_SIZE,
          clientId,
        ),
    });

  const clientForms = clientFormsData?.items ?? [];

  const navigate = useNavigate();

  return (
    <Card
      ref={containerRef}
      className={clsx(s.root, className, JourneyAnchors.CLIENT_FORMS)}
      elevation={0}
      variant="outlined"
      {...other}
    >
      <ClientDetailsCardHeader
        icon={<ProfileIconBlock icon={<FormsIcon />} bgVariant="accent" />}
        title="Forms"
        isOpenContent={isOpenContent}
        setIsOpenContent={setIsOpenContent}
        isPreview={isPreview}
        isFirst={isFirst}
        collapsable={collapsable}
        defaultOpen={defaultOpen}
      />

      {isOpenContent && (
        <Box className={s.contentWrapper}>
          {clientForms.length ? (
            clientForms.map((clientForm) => (
              <ClientFormCardItem
                key={clientForm.formId}
                clientForm={clientForm}
              />
            ))
          ) : clientFormsQueryInFlight ? (
            Array.from({ length: 3 }).map((_, i) => (
              <ClientFormCardItemSkeleton key={i} />
            ))
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                height: collapsable ? 320 : 180,
              }}
            >
              <Typography className={s.noText}>No forms to show.</Typography>
              {role === UserRole.COACH && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<SendHorizontal size={17} />}
                  onClick={() => navigate(COACH_CLIENTS_FORMS_ROUTE)}
                >
                  Send a form
                </Button>
              )}
            </Box>
          )}

          {clientFormsData?.totalPages > 1 && (
            <Box className={s.paginationWrapper}>
              <CardPagination
                page={currentPage}
                count={clientFormsData?.totalPages}
                onChange={(_, page) => setCurrentPage(page)}
              />
            </Box>
          )}
        </Box>
      )}
    </Card>
  );
}
